import { useEffect, useState, type FC } from 'react';

import Box from '@mui/material/Box';
import ImageNotSupportedOutlined from '@mui/icons-material/ImageNotSupportedOutlined';
import type { SxProps, Theme } from '@mui/material/styles';

interface Props {
  /** URL ảnh đã hoàn chỉnh (caller tự ghép với storage KIOS nếu API trả path tương đối). */
  src?: string | null;
  alt: string;
  /** sx cho thẻ img và cho ô giữ chỗ — dùng chung để khung không nhảy kích thước khi ảnh lỗi. */
  sx?: SxProps<Theme>;
  /** Cỡ icon giữ chỗ; để trống thì theo cỡ mặc định của icon. */
  fallbackIconSize?: number;
}

/**
 * Ảnh lấy từ storage KIOS. Storage này có những file trả 503/404 (dữ liệu KIOS thiếu file — không
 * phải ghép URL sai, vì ảnh khác trong cùng thư mục vẫn tải được), khi đó `<img>` vỡ và trình
 * duyệt hiện chữ alt trơ ra giữa bảng/thẻ. Component này rơi về icon "không có ảnh" cho gọn.
 * Đây là che triệu chứng: ảnh vẫn phải do phía KIOS bù.
 */
const KiosImage: FC<Props> = ({ src, alt, sx, fallbackIconSize }) => {
  const [failed, setFailed] = useState(false);

  // Ảnh đổi (đổi trang, đổi bộ lọc) thì cho phép thử tải lại, không giữ mãi trạng thái lỗi cũ.
  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          ...sx,
        }}
      >
        <ImageNotSupportedOutlined sx={{ color: 'text.disabled', fontSize: fallbackIconSize }} />
      </Box>
    );
  }

  return <Box component="img" src={src} alt={alt} onError={() => setFailed(true)} sx={sx} />;
};

export default KiosImage;
